import type { FunctionComponent, SVGProps } from 'react';
import { useFela } from 'react-fela';

import type theme from '@/styles/theme';

import { Add } from './Add';
import { Admin } from './Admin';
import { Burger } from './Burger';
import { Caret } from './Caret';
import { Chat } from './Chat';
import { Checkmark } from './Checkmark';
import { Close } from './Close';
import { Copy } from './Copy';
import { CopyBig } from './CopyBig';
import { Delete } from './Delete';
import { Download } from './Download';
import { Dropdown } from './Dropdown';
import { Edit } from './Edit';
import { Export } from './Export';
import { Extension } from './Extension';
import { Eye } from './Eye';
import { EyeClose } from './EyeClose';
import { FAQ } from './FAQ';
import { Group } from './Group';
import { GroupVisible } from './GroupVisible';
import { Guide } from './Guide';
import { Hash } from './Hash';
import { container, IconType } from './Icon.rules';
import { ImportSmall } from './ImportSmall';
import { Info } from './Info';
import { InfoOutlined } from './InfoOutlined';
import { Link } from './Link';
import { Lock } from './Lock';
import { Logout } from './Logout';
import { New } from './New';
import { News } from './News';
import { NoReport } from './NoReport';
import { Passkey } from './Passkey';
import { Password } from './Password';
import { Premium } from './Premium';
import { QRCode } from './QRCode';
import { Reloading } from './Reloading';
import { Revert } from './Revert';
import { Save } from './Save';
import { Search } from './Search';
import { SecretTypeApiCredentials } from './SecretTypeApiCredentials';
import { SecretTypeDatabase } from './SecretTypeDatabase';
import { SecretTypePassword } from './SecretTypePassword';
import { SecretTypePaymentCard } from './SecretTypePaymentCard';
import { SecretTypeSecureNote } from './SecretTypeSecureNote';
import { SecretTypeSshKey } from './SecretTypeSshKey';
import { SecurityAudit } from './SecurityAudit';
import { Settings } from './Settings';
import { Spinner } from './Spinner';
import { StatusAlert } from './StatusAlert';
import { StatusBolt } from './StatusBolt';
import { StatusNoPass } from './StatusNoPass';
import { StatusOk } from './StatusOk';
import { StatusWarning } from './StatusWarning';
import { TOTP } from './TOTP';
import { Trash } from './Trash';
import { TwoUsers } from './TwoUsers';
import { Url } from './Url';
import { User } from './User';
import { Warning } from './Warning';
import { WarningInverse } from './WarningInverse';
import { WarningTriangle } from './WarningTriangle';
import { X } from './X';

const ICONS: Record<
    IconType,
    {
        className: string;
        src: FunctionComponent<SVGProps<SVGSVGElement>>;
    }
> = {
    [IconType.MENU]: { className: 'MenuIcon', src: Burger },
    [IconType.CLOSE]: { className: 'CloseIcon', src: Close },
    [IconType.USER]: { className: 'UserIcon', src: User },
    [IconType.LOGOUT]: { className: 'LogoutIcon', src: Logout },
    [IconType.COPY]: { className: 'CopyIcon', src: Copy },
    [IconType.COPY_BIG]: { className: 'CopyBigIcon', src: CopyBig },
    [IconType.ADD]: { className: 'AddIcon', src: Add },
    [IconType.PASSWORD]: { className: 'PasswordIcon', src: Password },
    [IconType.SEARCH]: { className: 'SearchIcon', src: Search },
    [IconType.EYE]: { className: 'EyeIcon', src: Eye },
    [IconType.EYE_CLOSE]: { className: 'EyeCloseIcon', src: EyeClose },
    [IconType.LINK]: { className: 'LinkIcon', src: Link },
    [IconType.EDIT]: { className: 'EditIcon', src: Edit },
    [IconType.SPINNER]: { className: 'SpinnerIcon', src: Spinner },
    [IconType.URL]: { className: 'UrlIcon', src: Url },
    [IconType.RELOADING]: { className: 'ReloadingIcon', src: Reloading },
    [IconType.EXPORT]: { className: 'ExportIcon', src: Export },
    [IconType.IMPORT_SMALL]: { className: 'ImportSmallIcon', src: ImportSmall },
    [IconType.DELETE]: { className: 'DeleteIcon', src: Delete },
    [IconType.SECURITY_AUDIT]: { className: 'SecurityAuditIcon', src: SecurityAudit },
    [IconType.GROUP_VISIBLE]: { className: 'GroupVisibleIcon', src: GroupVisible },
    [IconType.REVERT_CHANGES]: { className: 'RevertChangesIcon', src: Revert },
    [IconType.WARNING]: { className: 'WarningIcon', src: Warning },
    [IconType.WARNING_INVERSE]: { className: 'WarningInverseIcon', src: WarningInverse },
    [IconType.WARNING_TRIANGLE]: { className: 'WarningTriangleIcon', src: WarningTriangle },
    [IconType.EXTENSION]: { className: 'ExtensionIcon', src: Extension },
    [IconType.CARET]: { className: 'CaretIcon', src: Caret },
    [IconType.X]: { className: 'XIcon', src: X },
    [IconType.NEW]: { className: 'NewTagIcon', src: New },
    [IconType.DROPDOWN]: { className: 'DropdownIcon', src: Dropdown },
    [IconType.SETTINGS]: { className: 'SettingsIcon', src: Settings },
    [IconType.LOCK]: { className: 'LockIcon', src: Lock },
    [IconType.SAVE]: { className: 'SaveIcon', src: Save },
    [IconType.STATUS_ALERT]: { className: 'StatusAlertIcon', src: StatusAlert },
    [IconType.STATUS_WARNING]: { className: 'StatusWarningIcon', src: StatusWarning },
    [IconType.STATUS_OK]: { className: 'StatusOkIcon', src: StatusOk },
    [IconType.STATUS_BOLT]: {
        className: 'StatusBoltIcon',
        src: StatusBolt,
    },
    [IconType.STATUS_NO_PASS]: { className: 'NotSecurityLevelIcon', src: StatusNoPass },
    [IconType.TRASH]: { className: 'TrashIcon', src: Trash },
    [IconType.DOWNLOAD]: { className: 'DownloadIcon', src: Download },
    [IconType.FAQ]: { className: 'FAQIcon', src: FAQ },
    [IconType.NEWS]: { className: 'NewsIcon', src: News },
    [IconType.GUIDE]: { className: 'GuideIcon', src: Guide },
    [IconType.NO_REPORT]: { className: 'NoReportIcon', src: NoReport },
    [IconType.TOTP]: { className: 'TOTPIcon', src: TOTP },
    [IconType.CHAT]: { className: 'ChatIcon', src: Chat },
    [IconType.INFO]: { className: 'InfoIcon', src: Info },
    [IconType.GROUP]: { className: 'GroupIcon', src: Group },
    [IconType.ADMIN]: { className: 'AdminIcon', src: Admin },
    [IconType.HASH]: { className: 'HashIcon', src: Hash },
    [IconType.QR_CODE]: { className: 'QRCodeIcon', src: QRCode },

    [IconType.SECRET_TYPE_PASSWORD]: { className: 'SecretTypePasswordIcon', src: SecretTypePassword },
    [IconType.SECRET_TYPE_PAYMENT_CARD]: { className: 'SecretTypePaymentCardIcon', src: SecretTypePaymentCard },
    [IconType.SECRET_TYPE_API_CREDENTIALS]: {
        className: 'SecretTypeApiCredentialsIcon',
        src: SecretTypeApiCredentials,
    },
    [IconType.SECRET_TYPE_DATABASE]: { className: 'SecretTypeDatabaseIcon', src: SecretTypeDatabase },
    [IconType.SECRET_TYPE_SSH_KEY]: { className: 'SecretTypeSshKeyIcon', src: SecretTypeSshKey },
    [IconType.SECRET_TYPE_SECURE_NOTE]: { className: 'SecretTypeSecureNoteIcon', src: SecretTypeSecureNote },
    [IconType.CHECKMARK]: { className: 'CheckmarkIcon', src: Checkmark },
    [IconType.PREMIUM]: { className: 'PremiumIcon', src: Premium },
    [IconType.INFO_OUTLINED]: { className: 'InfoOutlinedIcon', src: InfoOutlined },
    [IconType.PASSKEY]: { className: 'PasskeyIcon', src: Passkey },
    [IconType.TWO_USERS]: { className: 'TwoUsersIcon', src: TwoUsers },
};

export { IconType };

export interface IconProps extends Pick<SVGProps<SVGSVGElement>, 'onClick' | 'width' | 'height' | 'aria-label' | 'className'> {
    type: `${IconType}`;
    color?: keyof typeof theme.colors | 'inherit';
    customStyle?: typeof container;
}

export const Icon = ({ type, color, onClick, width, height, 'aria-label': label, customStyle, className, ...restProps }: IconProps) => {
    const { css } = useFela({
        color,
    });

    const { src: CurrentIcon, className: iconClassName } = ICONS[type];

    const props: SVGProps<SVGSVGElement> = {
        ...restProps,
        onClick,
        className: `Icon ${iconClassName} ${className} ${css(container, customStyle)}`,
        'aria-label': label ?? `icon: ${type}`,
    };

    if (width) {
        props.width = width;
    }
    if (height) {
        props.height = height;
    }

    return <CurrentIcon {...props} />;
};
